
import {
  defineComponent, computed, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputSwitch from 'primevue/inputswitch';

interface CustomKpiMeasureMeta {
  key: string;
  name: string;
  type: string;
}
interface CustomKpiDimensionMeta {
  key: string;
  name: string;
}
export interface CustomKpiSelection {
  measures: string[];
  dimensions: string[];
  chartMode?: 'timeline' | 'distribution';
  chartSubType?: 'bar' | 'line' | 'pie' | 'doughnut' | 'radar';
}

export default defineComponent({
  name: 'CustomKpiWidgetDataConfig',
  components: {
    Dropdown,
    MultiSelect,
    InputSwitch,
  },
  props: {
    widgetType: {
      type: String as PropType<'kpi' | 'table' | 'chart'>,
      required: true,
    },
    measuresOptions: {
      type: Array as PropType<CustomKpiMeasureMeta[]>,
      required: true,
    },
    dimensionsOptions: {
      type: Array as PropType<CustomKpiDimensionMeta[]>,
      required: true,
    },
    supportsTimeline: {
      type: Boolean,
      default: true,
    },
    /**
     * Sélection courante (mesures + dimensions + chart options).
     * Mise à jour via update:selection.
     */
    selection: {
      type: Object as PropType<CustomKpiSelection>,
      required: true,
    },
    includeTotal: {
      type: Boolean,
      default: false,
    },
    totalOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:selection', 'update:includeTotal', 'update:totalOnly'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const selectedMeasures = computed(() => props.selection.measures ?? []);
    const selectedDimensions = computed(() => props.selection.dimensions ?? []);
    const selectedMeasure = computed(() => selectedMeasures.value[0] ?? null);
    const selectedDimension = computed(() => selectedDimensions.value[0] ?? null);

    const chartMode = computed(() => props.selection.chartMode ?? 'timeline');
    const chartSubType = computed(() => props.selection.chartSubType ?? 'bar');

    const kpiDimensionOptions = computed(() => [
      { key: '', name: t('customStats.widgetConfig.kpi.ventilateNone') },
      ...props.dimensionsOptions,
    ]);

    const distributionSubTypes = computed(() => [
      { value: 'pie', label: t('customStats.widgetConfig.chart.subTypePie'), icon: 'far fa-chart-pie' },
      { value: 'doughnut', label: t('customStats.widgetConfig.chart.subTypeDoughnut'), icon: 'fas fa-circle-notch' },
      { value: 'radar', label: t('customStats.widgetConfig.chart.subTypeRadar'), icon: 'far fa-chart-radar' },
      { value: 'bar', label: t('customStats.widgetConfig.chart.subTypeBar'), icon: 'far fa-chart-column' },
    ]);

    const updateSelection = (patch: Partial<CustomKpiSelection>) => {
      emit('update:selection', { ...props.selection, ...patch });
    };

    function onSelectSingleMeasure(key: string) {
      updateSelection({ measures: key ? [key] : [] });
    }

    function onUpdateMeasures(values: string[] | null) {
      updateSelection({ measures: values ?? [] });
    }

    function onUpdateDimensions(values: string[] | null) {
      updateSelection({ dimensions: values ?? [] });
    }

    const kpiBreakdownEnabled = computed(() => selectedDimensions.value.length > 0);

    function onToggleKpiBreakdown(v: boolean) {
      if (v) {
        if (selectedDimensions.value.length === 0 && props.dimensionsOptions[0]) {
          updateSelection({ dimensions: [props.dimensionsOptions[0].key] });
        }
      } else {
        updateSelection({ dimensions: [] });
        if (props.includeTotal) emit('update:includeTotal', false);
        if (props.totalOnly) emit('update:totalOnly', false);
      }
    }

    function onSelectKpiDimension(key: string) {
      updateSelection({ dimensions: key ? [key] : [] });
    }

    function onSelectChartDimension(key: string) {
      updateSelection({ dimensions: key ? [key] : [] });
    }

    function onSelectDistributionDimension(key: string) {
      updateSelection({ dimensions: key ? [key] : [] });
    }

    function setChartMode(mode: 'timeline' | 'distribution') {
      const patch: Partial<CustomKpiSelection> = { chartMode: mode };
      // Reset cohérent : si on passe en distribution, 1 seule mesure & 1 dim ;
      // si timeline, on peut garder multi-mesures.
      if (mode === 'distribution') {
        if (selectedMeasures.value.length > 1) {
          patch.measures = selectedMeasures.value.slice(0, 1);
        }
        if (selectedDimensions.value.length === 0) {
          patch.dimensions = props.dimensionsOptions[0]
            ? [props.dimensionsOptions[0].key]
            : [];
        } else if (selectedDimensions.value.length > 1) {
          patch.dimensions = selectedDimensions.value.slice(0, 1);
        }
        // Sub-type par défaut pour distribution
        if (
          chartSubType.value !== 'pie' &&
          chartSubType.value !== 'doughnut' &&
          chartSubType.value !== 'radar' &&
          chartSubType.value !== 'bar'
        ) {
          patch.chartSubType = 'pie';
        }
      } else {
        // Timeline : sub-type bar par défaut
        if (chartSubType.value !== 'bar' && chartSubType.value !== 'line') {
          patch.chartSubType = 'bar';
        }
      }
      updateSelection(patch);
    }

    function setChartSubType(t: string) {
      updateSelection({ chartSubType: t as any });
    }

    function onToggleIncludeTotal(v: boolean) {
      emit('update:includeTotal', v);
      if (!v) emit('update:totalOnly', false);
    }

    function onToggleTotalOnly(v: boolean) {
      emit('update:totalOnly', v);
    }

    return {
      t,
      selectedMeasures,
      selectedDimensions,
      selectedMeasure,
      selectedDimension,
      chartMode,
      chartSubType,
      kpiDimensionOptions,
      distributionSubTypes,
      kpiBreakdownEnabled,
      onToggleKpiBreakdown,
      onSelectSingleMeasure,
      onUpdateMeasures,
      onUpdateDimensions,
      onSelectKpiDimension,
      onSelectChartDimension,
      onSelectDistributionDimension,
      setChartMode,
      setChartSubType,
      onToggleIncludeTotal,
      onToggleTotalOnly,
    };
  },
});
